import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CLIP_STATUS_VALUES, type ClipStatus } from '@content-engine/shared';
import { fetchClips, fetchFilms } from '../lib/queries.ts';
import type { ClipListItem } from '../lib/types.ts';
import { AuthorFilter } from '../components/AuthorFilter.tsx';
import {
  ArchivedChip,
  FileChip,
  MediaPlaceholder,
  ParentLink,
  ShowArchivedCheckbox,
  isFreed,
} from '../components/archive/index.ts';

const PAGE_SIZE = 20;

/** Класс чипа статуса рендера. Красное — только настоящая беда (ошибка/отказ). */
function statusChipClass(status: ClipStatus): string {
  if (status === 'ready' || status === 'approved') return 'chip ok';
  if (status === 'error' || status === 'rejected') return 'chip err';
  return 'chip';
}

function hms(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return [h, m, r].map((n) => String(n).padStart(2, '0')).join(':');
}

export function ClipsPage(): React.ReactElement {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<ClipStatus | ''>('');
  const [filmId, setFilmId] = useState<string>('');
  const [search, setSearch] = useState('');
  // Поиск уходит на сервер (ILIKE по названию момента, до пагинации), поэтому
  // печатать в него дёшево только с дебаунсом — иначе запрос на каждый символ.
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [author, setAuthor] = useState<'me' | 'all' | string>('me');

  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const clipsQuery = useQuery({
    queryKey: ['clips', author, page, status, filmId, showArchived, searchTerm],
    queryFn: () =>
      fetchClips({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: status ? [status] : [],
        ...(filmId ? { filmId } : {}),
        createdBy: author,
        counts: true,
        showArchived,
        ...(searchTerm ? { search: searchTerm } : {}),
      }),
    refetchInterval: (q) => {
      const list = q.state.data?.clips ?? [];
      return list.some((c) => c.status === 'pending' || c.status === 'rendering') ? 3_000 : false;
    },
  });

  const clips = clipsQuery.data?.clips ?? [];
  const total = clipsQuery.data?.total ?? 0;
  const counts = clipsQuery.data?.counts ?? null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Фильмы для выпадашки «Все фильмы» — вместе с архивными: у архивного фильма
  // клипы живут своей жизнью, и отфильтровать их по нему нужно уметь.
  const filmsQuery = useQuery({
    queryKey: ['films', 'clipFilter', author],
    queryFn: () => fetchFilms({ createdBy: author, limit: 100, showArchived: true }),
    staleTime: 60_000,
  });
  const films = filmsQuery.data?.films ?? [];

  // Название и таймкоды клипа — это название и таймкоды его момента; бэкенд
  // отдаёт их прямо в строке (moment), отдельных запросов за моментами нет.
  const titleOf = (c: ClipListItem): string => c.moment?.title ?? t('clips.untitled');
  const rangeOf = (c: ClipListItem): string | null =>
    c.moment ? `${hms(Number(c.moment.startSec))} – ${hms(Number(c.moment.endSec))}` : null;

  const rows = clips;

  // Гидрации кеша ['clipVariants', …] здесь больше нет: ClipPreview на этой
  // странице не рендерится, а при showArchived бандл содержал бы и архивные
  // варианты — они отравляли бы общий кеш на staleTime карточки клипа.

  // Clamp page when total shrinks below current offset. Only after data has
  // actually arrived — otherwise the initial loading state (total=0,
  // totalPages=1) would clamp every Next-click back to 0.
  useEffect(() => {
    if (!clipsQuery.data) return;
    if (page >= totalPages) setPage(totalPages - 1);
  }, [totalPages, page, clipsQuery.data]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('clips.title')}</h1>
          <div className="sub">
            {t('clips.subtitleCounts', {
              active: counts?.active ?? 0,
              archived: counts?.archived ?? 0,
            })}
          </div>
        </div>
        <div className="filters">
          <AuthorFilter
            value={author}
            onChange={(v) => {
              setAuthor(v);
              setPage(0);
            }}
          />
        </div>
      </div>

      <p style={{ margin: '6px 2px 18px', color: 'var(--text-3)', fontSize: 12.5 }}>
        {t('clips.hint')}
      </p>

      <div
        className="filters"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}
      >
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ClipStatus | '');
            setPage(0);
          }}
        >
          <option value="">{t('clips.filters.allStatuses')}</option>
          {CLIP_STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {t(`clips.status_${s}`)} ({counts?.[s] ?? 0})
            </option>
          ))}
        </select>
        <select
          value={filmId}
          onChange={(e) => {
            setFilmId(e.target.value);
            setPage(0);
          }}
        >
          <option value="">{t('clips.filters.allFilms')}</option>
          {films.map((f) => (
            <option key={f.id} value={f.id}>
              {f.parsedTitle ?? f.rawName}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('clips.filters.searchMoment')}
          style={{ minWidth: 220 }}
        />
        <ShowArchivedCheckbox
          checked={showArchived}
          onChange={(v) => {
            setShowArchived(v);
            setPage(0);
          }}
        />
      </div>

      <div className="section">
        {clipsQuery.isLoading && <div style={{ padding: 16 }}>{t('common.loading')}</div>}
        {rows.length === 0 && !clipsQuery.isLoading && (
          <div style={{ padding: 24, color: 'var(--text-3)' }}>{t('clips.noClips')}</div>
        )}
        {rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{t('clips.table.clip')}</th>
                <th>{t('clips.table.film')}</th>
                <th>{t('clips.table.duration')}</th>
                <th>{t('clips.table.variants')}</th>
                <th>{t('clips.table.file')}</th>
                <th>{t('clips.table.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className={c.archivedAt ? 'arch' : undefined}>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {isFreed(c.file) ? (
                        <MediaPlaceholder file={c.file} variant="thumb" />
                      ) : (
                        <div className="thumb" />
                      )}
                      <div>
                        <div style={{ fontWeight: 600 }}>{titleOf(c)}</div>
                        <div style={{ color: 'var(--text-3)' }}>{rangeOf(c) ?? '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{c.film ? <ParentLink parent={c.film} kind="film" /> : '—'}</td>
                  <td style={{ color: 'var(--text-3)' }}>
                    {c.durationSec
                      ? t('clips.table.durationSec', { sec: Math.round(Number(c.durationSec)) })
                      : '—'}
                  </td>
                  <td>{c.variantsCount}</td>
                  <td>
                    <FileChip file={c.file} />
                  </td>
                  <td>
                    <span className={statusChipClass(c.status)}>{t(`clips.status_${c.status}`)}</span>{' '}
                    <ArchivedChip archivedAt={c.archivedAt} />
                  </td>
                  <td>
                    <Link to={`/clips/${c.id}`}>
                      <button type="button">{t('common.open')}</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {total > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              padding: 12,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← {t('common.prev')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {t('clips.pagination.rangeOf', {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, total),
                total,
              })}{' '}
              · {t('clips.pagination.pageOf', { current: page + 1, total: totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
            >
              {t('common.next')} →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
